import React, { useEffect, useState } from "react";
import { X, Search, ChevronLeft, Zap, ClipboardEdit, User } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * CreateMemberRequestModal — iter60 Golf Shop Concierge wizard.
 *
 * 3-step full wizard (Select Member → Item Details → Submit) OR Quick
 * Request mode (single compact form: member + short description +
 * source). Reuses existing tokens (cream, deep green, brass) — no new
 * design system.
 *
 * Props:
 *   open (bool)          — show/hide the modal
 *   onClose()            — dismiss
 *   onCreated(order)     — callback after successful submission
 *   preselectedMember?   — {user_id, name, ...} skips step 1
 */
export default function CreateMemberRequestModal({ open, onClose, onCreated, preselectedMember = null }) {
  const [mode, setMode] = useState("full"); // "full" | "quick"
  const [step, setStep] = useState(preselectedMember ? 2 : 1);
  const [member, setMember] = useState(preselectedMember);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState({
    request_source: "",
    item_name: "",
    brand: "",
    description: "",
    category: "Other",
    color: "",
    size: "",
    quantity: 1,
    logo_preference: "",
    notes: "",
    product_link: "",
    staff_internal_note: "",
  });
  const [busy, setBusy] = useState(null); // "draft" | "quick" | "send_for_review" | "submit_on_behalf"
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/staff-orders/sources").then(({ data }) => setSources(data.sources || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open || preselectedMember) return;
    const t = setTimeout(async () => {
      setLoadingMembers(true);
      try {
        const { data } = await api.get(`/admin/members${search.trim() ? `?search=${encodeURIComponent(search.trim())}` : ""}`);
        setMembers(data);
      } catch {
        setMembers([]);
      } finally {
        setLoadingMembers(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [open, search, preselectedMember]);

  const reset = () => {
    setStep(preselectedMember ? 2 : 1);
    setMember(preselectedMember);
    setSearch("");
    setError("");
    setConfirmOpen(false);
    setForm({
      request_source: "", item_name: "", brand: "", description: "",
      category: "Other", color: "", size: "", quantity: 1,
      logo_preference: "", notes: "", product_link: "", staff_internal_note: "",
    });
  };

  const close = () => { reset(); onClose?.(); };

  const submit = async (submission_mode) => {
    setError("");
    if (!member) { setError("Please select a member."); return; }
    if (!form.request_source) { setError("Please select how the request was received."); return; }
    if (submission_mode === "quick") {
      const hasDesc = form.description.trim() || form.item_name.trim();
      if (!hasDesc) { setError("Quick Request requires an item name or short description."); return; }
    }
    setBusy(submission_mode);
    try {
      const payload = {
        member_id: member.user_id,
        request_source: form.request_source,
        submission_mode,
        item_name: form.item_name || undefined,
        brand: form.brand || undefined,
        description: form.description || undefined,
        category: form.category || "Other",
        color: form.color || undefined,
        size: form.size || undefined,
        quantity: form.quantity || 1,
        logo_preference: form.logo_preference || undefined,
        notes: form.notes || undefined,
        product_link: form.product_link || undefined,
        staff_internal_note: form.staff_internal_note || undefined,
      };
      const { data } = await api.post("/admin/orders/staff-created", payload);
      onCreated?.(data);
      close();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setConfirmOpen(false);
    } finally {
      setBusy(null);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={close}
      data-testid="staff-request-modal"
    >
      <div
        className="bg-white max-w-3xl w-full max-h-[92vh] overflow-hidden border border-[#E5E3D8] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-8 py-5 border-b border-[#E5E3D8] flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[#B59A5A]">
              Golf Shop Concierge
            </div>
            <h2 className="mt-1 font-serif text-2xl text-[#1A422D]">
              Create Member Request
            </h2>
            <p className="mt-1 text-sm text-[#7A7A7A]">
              {mode === "quick" ? "Quick Request — capture in under a minute" : `Step ${step} of 3`}
            </p>
          </div>
          <button type="button" onClick={close} data-testid="staff-request-close" className="text-[#7A7A7A] hover:text-[#1A422D] text-2xl leading-none">×</button>
        </header>

        {/* Mode toggle */}
        <div className="px-8 pt-4 flex items-center gap-2">
          <ModeChip active={mode === "full"} onClick={() => { setMode("full"); reset(); }} testId="staff-request-mode-full">
            <ClipboardEdit size={13} /> Full Wizard
          </ModeChip>
          <ModeChip active={mode === "quick"} onClick={() => { setMode("quick"); reset(); setStep(preselectedMember ? 2 : 1); }} testId="staff-request-mode-quick">
            <Zap size={13} /> Quick Request
          </ModeChip>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          {error && (
            <div data-testid="staff-request-error" className="mb-4 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm">{error}</div>
          )}

          {step === 1 && !preselectedMember && (
            <div data-testid="staff-request-step-select-member">
              <Label>Select Member *</Label>
              <div className="mt-2 relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B59A5A]" />
                <input
                  data-testid="staff-request-search"
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, email, phone, or member number"
                  className="w-full pl-9 input-field"
                  autoFocus
                />
              </div>
              <div className="mt-4 max-h-80 overflow-y-auto border border-[#E5E3D8]" data-testid="staff-request-member-list">
                {loadingMembers ? (
                  <div className="p-6 text-center text-sm text-[#7A7A7A]">Searching…</div>
                ) : members.length === 0 ? (
                  <div className="p-6 text-center text-sm text-[#7A7A7A]">
                    No members match — try a different search.
                  </div>
                ) : (
                  members.map((m) => (
                    <button
                      key={m.user_id}
                      type="button"
                      onClick={() => { setMember(m); setStep(2); }}
                      data-testid={`staff-request-member-${m.user_id}`}
                      className="w-full text-left px-5 py-3 border-b border-[#F0EDE0] last:border-0 hover:bg-[#F9F8F4] transition-colors flex items-center gap-3"
                    >
                      <User size={16} className="text-[#B59A5A]" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[#1A422D] font-medium truncate">{m.name || m.email}</div>
                        <div className="text-xs text-[#7A7A7A] truncate">
                          {m.email}{m.clubs?.[0]?.member_number ? ` · #${m.clubs[0].member_number}` : ""}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {step >= 2 && member && (
            <div>
              {/* Member card */}
              <div data-testid="staff-request-member-card" className="p-4 bg-[#F1EFE6] border border-[#D9C68A]/50 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#1A422D] text-[#D9C68A] flex items-center justify-center">
                  <User size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#8A6A2A]">Applying request for</div>
                  <div className="font-serif text-lg text-[#1A422D]">{member.name || member.email}</div>
                  <div className="text-xs text-[#7A7A7A]">
                    {member.email}{member.clubs?.[0]?.member_number ? ` · #${member.clubs[0].member_number}` : ""}
                  </div>
                </div>
                {!preselectedMember && (
                  <button type="button" onClick={() => { setMember(null); setStep(1); }} className="text-xs uppercase tracking-widest text-[#7A7A7A] hover:text-[#1A422D]">
                    Change
                  </button>
                )}
              </div>

              {/* Request source */}
              <div className="mt-6">
                <Label>How was this request received? *</Label>
                <div data-testid="staff-request-source" className="mt-2 flex flex-wrap gap-2">
                  {sources.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, request_source: s }))}
                      data-testid={`staff-request-source-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`px-4 py-2 border text-sm ${form.request_source === s ? "bg-[#1A422D] text-[#F9F8F4] border-[#1A422D]" : "bg-white text-[#4A4A4A] border-[#E5E3D8] hover:border-[#1A422D]"}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {mode === "quick" ? (
                // -------- Quick Request: minimal fields --------
                <>
                  <div className="mt-6">
                    <Label>Short Description *</Label>
                    <textarea
                      data-testid="staff-request-quick-description"
                      rows={3}
                      value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      placeholder="Navy quarter-zip, XL, wants club crest…"
                      className="mt-2 input-field"
                    />
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <TextInput testId="staff-request-quick-link" label="Product Link (optional)" value={form.product_link} onChange={(v) => setForm((f) => ({ ...f, product_link: v }))} placeholder="https://…" />
                    <TextInput testId="staff-request-quick-item" label="Item Name (optional)" value={form.item_name} onChange={(v) => setForm((f) => ({ ...f, item_name: v }))} />
                  </div>
                </>
              ) : (
                // -------- Full Wizard: step 2 (item details) + step 3 (submit) --------
                <>
                  {step === 2 && (
                    <div>
                      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <TextInput testId="staff-request-item-name" label="Item Name" value={form.item_name} onChange={(v) => setForm((f) => ({ ...f, item_name: v }))} />
                        <TextInput testId="staff-request-brand" label="Brand" value={form.brand} onChange={(v) => setForm((f) => ({ ...f, brand: v }))} />
                      </div>
                      <div className="mt-4">
                        <Label>Description / Details</Label>
                        <textarea
                          data-testid="staff-request-description"
                          rows={3}
                          value={form.description}
                          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                          className="mt-2 input-field"
                        />
                      </div>
                      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <SelectInput testId="staff-request-category" label="Category" value={form.category} onChange={(v) => setForm((f) => ({ ...f, category: v }))} options={["Apparel","Headwear","Footwear","Accessories","Golf Equipment","Travel","Gifts","Other"]} />
                        <TextInput testId="staff-request-size" label="Size" value={form.size} onChange={(v) => setForm((f) => ({ ...f, size: v }))} />
                        <TextInput testId="staff-request-color" label="Color" value={form.color} onChange={(v) => setForm((f) => ({ ...f, color: v }))} />
                        <NumberInput testId="staff-request-qty" label="Qty" value={form.quantity} onChange={(v) => setForm((f) => ({ ...f, quantity: v }))} />
                      </div>
                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <TextInput testId="staff-request-logo" label="Crest / Logo Preference" value={form.logo_preference} onChange={(v) => setForm((f) => ({ ...f, logo_preference: v }))} />
                        <TextInput testId="staff-request-link" label="Product Link (optional)" value={form.product_link} onChange={(v) => setForm((f) => ({ ...f, product_link: v }))} />
                      </div>
                      <div className="mt-4">
                        <Label>Special Instructions (visible to member)</Label>
                        <textarea
                          data-testid="staff-request-instructions"
                          rows={2}
                          value={form.notes}
                          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                          placeholder="e.g. Left chest crest · No embroidery · Match photo…"
                          className="mt-2 input-field"
                        />
                      </div>

                      <div className="mt-4">
                        <Label className="text-[#8a1a1a]">Internal Notes: Not Visible to Member</Label>
                        <textarea
                          data-testid="staff-request-internal-note"
                          rows={2}
                          value={form.staff_internal_note}
                          onChange={(e) => setForm((f) => ({ ...f, staff_internal_note: e.target.value }))}
                          className="mt-2 input-field bg-[#fbe9e9]/40"
                          placeholder="Staff-only. Never sent to the member."
                        />
                      </div>

                      <div className="mt-8 flex justify-end">
                        <button
                          type="button"
                          onClick={() => setStep(3)}
                          disabled={!form.request_source}
                          data-testid="staff-request-continue"
                          className="inline-flex items-center gap-2 px-6 py-3 bg-[#1A422D] text-[#F9F8F4] text-[12px] uppercase tracking-[0.22em] font-semibold hover:bg-[#0F2A1C] transition-colors disabled:opacity-50"
                        >
                          Continue <span aria-hidden>→</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div data-testid="staff-request-step-submit">
                      <button
                        type="button"
                        onClick={() => setStep(2)}
                        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#7A7A7A] hover:text-[#1A422D]"
                      >
                        <ChevronLeft size={13} /> Back
                      </button>
                      <div className="mt-4 p-6 bg-[#F9F8F4] border border-[#E5E3D8]">
                        <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#B59A5A]">Review</div>
                        <div className="mt-2 font-serif text-lg text-[#1A422D]">
                          {form.item_name || form.description?.slice(0, 60) || "(unnamed item)"} · Qty {form.quantity}
                        </div>
                        <div className="mt-1 text-sm text-[#4A4A4A]">
                          {[form.brand, form.category, form.color, form.size].filter(Boolean).join(" · ")}
                        </div>
                        {form.staff_internal_note && (
                          <div className="mt-3 text-xs text-[#8a1a1a] italic">
                            Internal note attached — not visible to member.
                          </div>
                        )}
                      </div>
                      <div className="mt-6 text-sm text-[#4A4A4A]">Choose how to submit:</div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        {step >= 2 && member && (
          <footer className="px-8 py-5 border-t border-[#E5E3D8] bg-[#FAF9F2] flex flex-wrap items-center justify-end gap-3">
            {mode === "quick" ? (
              <button
                type="button"
                onClick={() => submit("quick")}
                disabled={busy || !form.request_source}
                data-testid="staff-request-save-quick"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#1A422D] text-[#F9F8F4] text-[12px] uppercase tracking-[0.22em] font-semibold hover:bg-[#0F2A1C] transition-colors disabled:opacity-50"
              >
                {busy === "quick" ? "Saving…" : "Save & Finish Later"}
              </button>
            ) : step === 3 ? (
              <>
                <button
                  type="button"
                  onClick={() => submit("draft")}
                  disabled={busy}
                  data-testid="staff-request-save-draft"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-[#1A422D] text-[#1A422D] text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#1A422D] hover:text-[#F9F8F4] transition-colors disabled:opacity-50"
                >
                  {busy === "draft" ? "…" : "Save as Draft"}
                </button>
                <button
                  type="button"
                  onClick={() => submit("send_for_review")}
                  disabled={busy}
                  data-testid="staff-request-send-review"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-[#D9C68A] bg-[#D9C68A]/20 text-[#8A6A2A] text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#D9C68A] hover:text-[#1A422D] transition-colors disabled:opacity-50"
                >
                  {busy === "send_for_review" ? "…" : "Send to Member for Review"}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmOpen(true)}
                  disabled={busy}
                  data-testid="staff-request-submit-behalf"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[#1A422D] text-[#F9F8F4] text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#0F2A1C] transition-colors disabled:opacity-50"
                >
                  Submit on Member&rsquo;s Behalf <span aria-hidden>→</span>
                </button>
              </>
            ) : null}
          </footer>
        )}
      </div>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
          onClick={() => setConfirmOpen(false)}
          data-testid="staff-request-confirm-modal"
        >
          <div className="bg-white max-w-md w-full p-8 border border-[#E5E3D8]" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-xl text-[#1A422D]">Submit on Member&rsquo;s Behalf</h3>
            <p className="mt-3 text-sm text-[#4A4A4A]">
              You are submitting this special-order request on behalf of <strong>{member?.name}</strong>. This will enter the normal fulfillment workflow immediately.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 border border-[#E5E3D8] text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4A4A4A]"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="staff-request-confirm-submit"
                onClick={() => submit("submit_on_behalf")}
                disabled={busy}
                className="px-5 py-2 bg-[#1A422D] text-[#F9F8F4] text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#0F2A1C] disabled:opacity-50"
              >
                {busy === "submit_on_behalf" ? "Submitting…" : "Confirm & Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ModeChip({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold border transition-colors ${active ? "bg-[#1A422D] text-[#F9F8F4] border-[#1A422D]" : "bg-white text-[#4A4A4A] border-[#E5E3D8] hover:border-[#1A422D]"}`}
    >
      {children}
    </button>
  );
}

function Label({ children, className = "" }) {
  return <label className={`block text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4A4A4A] ${className}`}>{children}</label>;
}
function TextInput({ testId, label, value, onChange, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <input data-testid={testId} type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-2 input-field" />
    </div>
  );
}
function NumberInput({ testId, label, value, onChange }) {
  return (
    <div>
      <Label>{label}</Label>
      <input data-testid={testId} type="number" min={1} max={999} value={value} onChange={(e) => onChange(parseInt(e.target.value, 10) || 1)} className="mt-2 input-field" />
    </div>
  );
}
function SelectInput({ testId, label, value, onChange, options }) {
  return (
    <div>
      <Label>{label}</Label>
      <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 input-field">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
